import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { API } from '../config/api';
import {
  Music, Search, ChevronRight, ChevronLeft, Plus, Hash,
  TrendingUp, Loader2, Play, Eye, Users, Disc3, ArrowLeft
} from 'lucide-react';

export default function GenreBrowserPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isAuthenticated, getAuthHeaders } = useAuth();

  const [taxonomy, setTaxonomy] = useState({});
  const [categories, setCategories] = useState([]);
  const [allGenres, setAllGenres] = useState({});
  const [subGenres, setSubGenres] = useState([]);
  const [tracks, setTracks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingTracks, setLoadingTracks] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Navigation state from URL params
  const selectedCategory = searchParams.get('category') || null;
  const selectedGenre = searchParams.get('genre') || null;

  // New sub-genre form
  const [showNewSubGenre, setShowNewSubGenre] = useState(false);
  const [newSubGenreName, setNewSubGenreName] = useState('');
  const [creatingSubGenre, setCreatingSubGenre] = useState(false);

  useEffect(() => {
    fetchTaxonomy();
  }, []);

  useEffect(() => {
    if (selectedGenre) {
      fetchSubGenres(selectedGenre);
      fetchTracksByGenre(selectedGenre);
    }
  }, [selectedGenre]);

  const fetchTaxonomy = async () => {
    try {
      const res = await fetch(`${API}/reference/genre-taxonomy`);
      if (res.ok) {
        const data = await res.json();
        setTaxonomy(data.taxonomy || {});
        setAllGenres(data.all_genres || {});
        setCategories(data.categories || []);
      }
    } catch (err) {
      console.error('Failed to load taxonomy:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchSubGenres = async (genreId) => {
    try {
      const res = await fetch(`${API}/reference/sub-genres?parent_genre_id=${genreId}`);
      if (res.ok) {
        const data = await res.json();
        setSubGenres(data.sub_genres || []);
      }
    } catch (err) {
      console.error('Failed to load sub-genres:', err);
    }
  };

  const fetchTracksByGenre = async (genreId) => {
    setLoadingTracks(true);
    try {
      const res = await fetch(`${API}/tracks?genre=${genreId}&limit=12`);
      if (res.ok) {
        const data = await res.json();
        setTracks(Array.isArray(data) ? data : (data.tracks || []));
      }
    } catch (err) {
      console.error('Failed to load tracks:', err);
    } finally {
      setLoadingTracks(false);
    }
  };

  const handleCreateSubGenre = async () => {
    if (!newSubGenreName.trim() || !selectedGenre) return;
    setCreatingSubGenre(true);
    try {
      const res = await fetch(`${API}/reference/sub-genres`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({
          name: newSubGenreName.trim(),
          parent_genre_id: selectedGenre,
        }),
      });
      if (res.ok) {
        const data = await res.json();
        if (data.existing) {
          toast.info('This sub-genre already exists');
        } else {
          toast.success(`Sub-genre "${newSubGenreName}" created!`);
        }
        setNewSubGenreName('');
        setShowNewSubGenre(false);
        fetchSubGenres(selectedGenre);
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to create sub-genre');
      }
    } catch {
      toast.error('Failed to create sub-genre');
    } finally {
      setCreatingSubGenre(false);
    }
  };

  const setCategory = (catId) => {
    setSearchParams(catId ? { category: catId } : {});
  };

  const setGenre = (genreId) => {
    if (genreId) {
      setSearchParams({ category: selectedCategory, genre: genreId });
    } else {
      setSearchParams(selectedCategory ? { category: selectedCategory } : {});
    }
  };

  // Filter genres by search
  const filteredGenres = Object.entries(allGenres).filter(([id, g]) =>
    !searchQuery || g.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Genre detail view
  if (selectedGenre && allGenres[selectedGenre]) {
    const genre = allGenres[selectedGenre];
    return (
      <div className="min-h-screen py-8 px-4 sm:px-6 lg:px-8" data-testid="genre-detail-view">
        <div className="max-w-6xl mx-auto">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setGenre(null)}
            className="mb-4"
            data-testid="back-to-genres"
          >
            <ArrowLeft className="w-4 h-4 mr-1" /> Back to Genres
          </Button>

          <div className="flex items-center gap-4 mb-8">
            <div className="w-16 h-16 rounded-xl flex items-center justify-center text-3xl"
                 style={{ backgroundColor: `${genre.color || '#9C27B0'}20` }}>
              {genre.emoji || '🎵'}
            </div>
            <div>
              <h1 className="text-3xl sm:text-4xl font-black uppercase">{genre.name}</h1>
              <p className="text-muted-foreground text-sm mt-1">
                {tracks.length} tracks &middot; {subGenres.length} sub-genres
              </p>
            </div>
          </div>

          {/* Sub-Genres Section */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-bold flex items-center gap-2">
                <Hash className="w-4 h-4 text-secondary" /> Sub-Genres
              </h2>
              {isAuthenticated && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowNewSubGenre(!showNewSubGenre)}
                  data-testid="toggle-create-subgenre"
                >
                  <Plus className="w-4 h-4 mr-1" /> Add Sub-Genre
                </Button>
              )}
            </div>

            {showNewSubGenre && (
              <Card className="p-4 mb-4 border-secondary/30" data-testid="create-subgenre-form">
                <div className="flex gap-2">
                  <Input
                    value={newSubGenreName}
                    onChange={(e) => setNewSubGenreName(e.target.value)}
                    placeholder="New sub-genre name..."
                    className="flex-1"
                    maxLength={50}
                    data-testid="subgenre-name-input"
                  />
                  <Button
                    onClick={handleCreateSubGenre}
                    disabled={!newSubGenreName.trim() || creatingSubGenre}
                    className="bg-secondary hover:bg-secondary/90"
                    data-testid="submit-subgenre-btn"
                  >
                    {creatingSubGenre ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create'}
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  Add a custom sub-genre under {genre.name}. Other artists can use it too.
                </p>
              </Card>
            )}

            <div className="flex flex-wrap gap-2">
              {subGenres.length === 0 ? (
                <p className="text-sm text-muted-foreground">No sub-genres yet. Be the first to create one!</p>
              ) : (
                subGenres.map(sg => (
                  <Badge
                    key={sg.sub_genre_id}
                    variant="outline"
                    className="px-3 py-1.5 text-sm cursor-pointer hover:bg-muted/50 transition-colors"
                    data-testid={`subgenre-badge-${sg.sub_genre_id}`}
                  >
                    <Hash className="w-3 h-3 mr-1" />
                    {sg.name}
                    {sg.usage_count > 0 && (
                      <span className="ml-1 text-xs text-muted-foreground">({sg.usage_count})</span>
                    )}
                  </Badge>
                ))
              )}
            </div>
          </div>

          {/* Tracks in Genre */}
          <div>
            <h2 className="text-lg font-bold mb-3 flex items-center gap-2">
              <Music className="w-4 h-4 text-secondary" /> Tracks
            </h2>
            {loadingTracks ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
              </div>
            ) : tracks.length === 0 ? (
              <Card className="p-8 text-center border-dashed border-border">
                <Music className="w-10 h-10 text-muted-foreground/30 mx-auto mb-3" />
                <p className="text-muted-foreground">No tracks in this genre yet</p>
              </Card>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {tracks.map(track => (
                  <Card
                    key={track.track_id}
                    className="p-3 hover:ring-1 hover:ring-primary/30 transition-all cursor-pointer"
                    onClick={() => navigate(`/track/${track.track_id}`)}
                    data-testid={`genre-track-${track.track_id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-lg bg-muted/30 flex items-center justify-center overflow-hidden flex-shrink-0">
                        {track.cover_art_url ? (
                          <img src={track.cover_art_url} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <Disc3 className="w-6 h-6 text-muted-foreground/30" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{track.title}</p>
                        <p className="text-xs text-muted-foreground truncate">{track.artist_name || 'Unknown Artist'}</p>
                      </div>
                      <div className="text-xs text-muted-foreground flex items-center gap-1">
                        <Play className="w-3 h-3" /> {track.plays || 0}
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Category view (show genres in selected category)
  if (selectedCategory && taxonomy[selectedCategory]) {
    const catGenres = taxonomy[selectedCategory];
    const catInfo = categories.find(c => c.id === selectedCategory);

    return (
      <div className="min-h-screen py-8 px-4 sm:px-6 lg:px-8" data-testid="genre-category-view">
        <div className="max-w-6xl mx-auto">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setCategory(null)}
            className="mb-4"
            data-testid="back-to-categories"
          >
            <ArrowLeft className="w-4 h-4 mr-1" /> All Categories
          </Button>

          <h1 className="text-3xl sm:text-4xl font-black uppercase mb-2">
            {catInfo?.name || selectedCategory}
          </h1>
          <p className="text-muted-foreground mb-8">
            {Object.keys(catGenres).length} genres in this category
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {Object.entries(catGenres).map(([genreId, genreData]) => (
              <Card
                key={genreId}
                className="p-4 hover:ring-1 hover:ring-secondary/30 transition-all cursor-pointer group"
                onClick={() => setGenre(genreId)}
                data-testid={`genre-card-${genreId}`}
              >
                <div className="flex items-center gap-3">
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl group-hover:scale-110 transition-transform"
                    style={{ backgroundColor: `${genreData.color || '#9C27B0'}20` }}
                  >
                    {genreData.emoji || '🎵'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-sm">{genreData.name}</p>
                    {genreData.sub_genres && (
                      <p className="text-xs text-muted-foreground">{genreData.sub_genres.length} sub-genres</p>
                    )}
                  </div>
                  <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-secondary transition-colors" />
                </div>
              </Card>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Main view: All categories + search
  return (
    <div className="min-h-screen py-8 px-4 sm:px-6 lg:px-8" data-testid="genre-browser-page">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-10">
          <Badge className="mb-3 bg-secondary/20 text-secondary">Browse</Badge>
          <h1 className="text-3xl sm:text-4xl font-black uppercase mb-2">
            Explore <span className="text-secondary">Genres</span>
          </h1>
          <p className="text-muted-foreground max-w-lg mx-auto">
            Discover music across {Object.keys(allGenres).length} genres and create custom sub-genres.
          </p>
        </div>

        {/* Search */}
        <div className="relative max-w-md mx-auto mb-10">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search genres..."
            className="pl-10"
            data-testid="genre-search-input"
          />
        </div>

        {/* Search results */}
        {searchQuery && (
          <div className="mb-10" data-testid="genre-search-results">
            <h2 className="text-lg font-bold mb-3">
              Results for "{searchQuery}" ({filteredGenres.length})
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {filteredGenres.slice(0, 20).map(([genreId, genre]) => (
                <Card
                  key={genreId}
                  className="p-3 hover:ring-1 hover:ring-secondary/30 cursor-pointer"
                  onClick={() => setGenre(genreId)}
                  data-testid={`search-result-${genreId}`}
                >
                  <div className="flex items-center gap-2">
                    <span className="text-lg">{genre.emoji || '🎵'}</span>
                    <span className="font-medium text-sm truncate">{genre.name}</span>
                  </div>
                </Card>
              ))}
              {filteredGenres.length === 0 && (
                <p className="col-span-full text-center text-muted-foreground py-8">No genres match your search</p>
              )}
            </div>
          </div>
        )}

        {/* Categories Grid */}
        {!searchQuery && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="genre-categories-grid">
            {categories.map(cat => {
              const catGenres = taxonomy[cat.id] || {};
              const genreCount = Object.keys(catGenres).length;
              const previewGenres = Object.values(catGenres).slice(0, 4);

              return (
                <Card
                  key={cat.id}
                  className="p-5 hover:ring-1 hover:ring-secondary/30 transition-all cursor-pointer group"
                  onClick={() => setCategory(cat.id)}
                  data-testid={`category-card-${cat.id}`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-bold text-sm">{cat.name}</h3>
                    <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-secondary transition-colors" />
                  </div>
                  <div className="flex flex-wrap gap-1 mb-3">
                    {previewGenres.map((g, i) => (
                      <span key={i} className="text-lg">{g.emoji || '🎵'}</span>
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground">{genreCount} genres</p>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
